import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  useDissociateFromPatient,
  useGetDevices,
  usePutDeviceById,
} from "../../DeviceComponents";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { prettify, renderDeviceName } from "../../../utils/utils";

function AssociateDeviceDialog({ open, patient_id, onClose }) {
  const [deviceId, setDeviceId] = useState("");
  const [alreadyAssociatedAlert, setAlreadyAssociatedAlert] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const devicesQuery = useGetDevices("all");
  const linkMutation = usePutDeviceById();
  const dissociateMutation = useDissociateFromPatient();

  const devices = devicesQuery.data?.devices ?? [];
  const selectedDevice = devices.find((d) => d.id === Number(deviceId));
  const selectedPatientId =
    selectedDevice?.patient_id != null
      ? Number(selectedDevice.patient_id)
      : null;
  const currentPatientId = Number(patient_id);
  const isLinkedToThisPatient =
    selectedPatientId != null && selectedPatientId === currentPatientId;
  const isLinkedToOtherPatient =
    selectedPatientId != null && selectedPatientId !== currentPatientId;
  const isLinkedToKit =
    selectedDevice?.kit_id != null && selectedDevice?.kit_id !== "";

  const getDeviceAssociationLabel = (device) => {
    if (device.kit_id != null && device.kit_id !== "") {
      const kitLabel = device.kit_name
        ? prettify(device.kit_name)
        : `kit #${device.kit_id}`;
      return ` (kit: ${kitLabel})`;
    }
    if (device.patient_id != null) {
      return ` (${device.patient_info})`;
    }
    return " (libero)";
  };

  const handleClose = () => {
    setDeviceId("");
    setAlreadyAssociatedAlert(null);
    setConfirmDisconnect(false);
    onClose();
  };

  const handleAction = () => {
    if (!selectedDevice) return;

    if (isLinkedToOtherPatient) {
      setAlreadyAssociatedAlert(
        `Il dispositivo "${prettify(selectedDevice.name)}" è già associato a ${selectedDevice.patient_info}.`
      );
      return;
    }

    if (isLinkedToKit) {
      const kitLabel = selectedDevice.kit_name
        ? prettify(selectedDevice.kit_name)
        : `kit #${selectedDevice.kit_id}`;
      setAlreadyAssociatedAlert(
        `Il dispositivo "${prettify(selectedDevice.name)}" è già associato al kit "${kitLabel}".`
      );
      return;
    }

    if (isLinkedToThisPatient) {
      setConfirmDisconnect(true);
      return;
    }

    linkMutation.mutate(
      { id: selectedDevice.id, patient_id },
      { onSuccess: handleClose }
    );
  };

  const handleConfirmDisconnect = () => {
    if (!selectedDevice) return;
    dissociateMutation.mutate(selectedDevice, {
      onSuccess: handleClose,
    });
  };

  const isLoading = linkMutation.isLoading || dissociateMutation.isLoading;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Associa / disconnetti dispositivo</DialogTitle>
        <DialogContent>
          {devicesQuery.isLoading ? (
            <LoadingPaper />
          ) : devicesQuery.isError ? (
            <LoadingError error={devicesQuery.error} />
          ) : devices.length === 0 ? (
            <Typography>Nessun dispositivo disponibile.</Typography>
          ) : (
            <>
              <TextField
                select
                autoFocus
                fullWidth
                margin="dense"
                label="Seleziona dispositivo"
                variant="standard"
                value={deviceId}
                onChange={(e) => {
                  setDeviceId(e.target.value);
                  setAlreadyAssociatedAlert(null);
                }}
              >
                <MenuItem value="">
                  <em>Seleziona un dispositivo</em>
                </MenuItem>
                {devices.map((device) => (
                  <MenuItem key={device.id} value={device.id}>
                    {prettify(device.name)} — {renderDeviceName(device.device_type)}
                    {getDeviceAssociationLabel(device)}
                  </MenuItem>
                ))}
              </TextField>

              {selectedDevice && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {isLinkedToThisPatient &&
                    "Questo dispositivo è associato a questo paziente. Puoi disconnetterlo."}
                  {isLinkedToOtherPatient &&
                    `Attenzione: associato a ${selectedDevice.patient_info}.`}
                  {isLinkedToKit &&
                    `Attenzione: associato al kit ${
                      selectedDevice.kit_name
                        ? prettify(selectedDevice.kit_name)
                        : `#${selectedDevice.kit_id}`
                    }.`}
                  {!isLinkedToThisPatient &&
                    !isLinkedToOtherPatient &&
                    !isLinkedToKit &&
                    "Dispositivo libero, pronto per l'associazione."}
                </Typography>
              )}

              {alreadyAssociatedAlert && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {alreadyAssociatedAlert}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={isLinkedToThisPatient ? "error" : "primary"}
            disabled={
              isLoading || !selectedDevice || devicesQuery.isLoading
            }
          >
            {isLinkedToThisPatient ? "Disconnetti" : "Associa"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDisconnect}
        title="Disconnetti dispositivo"
        message={`Vuoi disconnettere "${selectedDevice?.name ?? ""}" da questo paziente?`}
        confirmLabel="Disconnetti"
        onCancel={() => setConfirmDisconnect(false)}
        onConfirm={handleConfirmDisconnect}
      />
    </>
  );
}

export default AssociateDeviceDialog;
